import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Button, Card, Input, Label, Notice, SectionTitle } from "../../ui/index.js";

/** Поле со ссылкой и кнопкой «Скопировать». */
export function CopyField({ label, value }: { label: string; value: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const id = `copy-${label.replace(/\W+/g, "-")}`;
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <div className="flex gap-2">
        <Input id={id} readOnly value={value} onFocus={(e) => e.target.select()} className="py-2 text-sm" />
        <Button
          variant="secondary"
          onClick={() =>
            void navigator.clipboard
              .writeText(value)
              .then(() => setCopied(true))
              .catch(() => setCopied(false))
          }
        >
          {copied ? t("onb.links.copied") : t("onb.links.copy")}
        </Button>
      </div>
    </div>
  );
}

/** QR страницы записи (для зеркала и стойки): PNG можно скачать и распечатать. */
export function BookingQr({ url, salonName }: { url: string; salonName: string }) {
  const { t } = useTranslation();
  const [png, setPng] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    // Библиотеку QR грузим лениво: она нужна только владельцу на этом экране, а не каждому посетителю страницы записи.
    import("qrcode")
      .then(({ default: QRCode }) => QRCode.toDataURL(url, { width: 768, margin: 2, errorCorrectionLevel: "M" }))
      .then((d) => alive && setPng(d))
      .catch(() => alive && setPng(null));
    return () => {
      alive = false;
    };
  }, [url]);
  if (!png) return null;
  return (
    <div className="text-center">
      <img src={png} alt={t("onb.links.qrAlt")} className="mx-auto h-56 w-56" />
      <a href={png} download={`${salonName || "booking"}-qr.png`} className="mt-2 inline-block text-sm text-slate-600 underline hover:text-slate-900">
        {t("onb.links.downloadQr")}
      </a>
    </div>
  );
}

/** Ссылки салона: страница записи (ссылка + QR), уведомление о данных, подсказка про главный экран. */
export function ShareLinks({
  bookingUrl,
  noticeUrl,
  salonName,
  closed,
  billingPath,
}: {
  bookingUrl: string;
  noticeUrl: string;
  salonName: string;
  /** Страница записи сейчас закрыта (нет подписки или салон на паузе): ссылкой ещё рано делиться. */
  closed: boolean;
  billingPath: string;
}) {
  const { t } = useTranslation();
  return (
    <div>
      {closed && (
        <div className="mb-4">
          <Notice kind="error">
            {t("onb.links.closed")}{" "}
            <Link to={billingPath} className="underline">
              {t("onb.links.closedAction")}
            </Link>
          </Notice>
        </div>
      )}
      <div className="lg:grid lg:grid-cols-2 lg:items-start lg:gap-6">
        <Card className="space-y-4">
          <CopyField label={t("onb.links.booking")} value={bookingUrl} />
          <p className="text-sm text-slate-500">{t("onb.links.bookingHint")}</p>
          <BookingQr url={bookingUrl} salonName={salonName} />
          <a href={bookingUrl} target="_blank" rel="noreferrer" className="block text-center text-sm text-slate-600 underline hover:text-slate-900">
            {t("onb.links.open")}
          </a>
        </Card>
        <div>
          <Card className="mt-4 space-y-2 lg:mt-0">
            <CopyField label={t("onb.links.notice")} value={noticeUrl} />
            <p className="text-sm text-slate-500">{t("onb.links.noticeHint")}</p>
          </Card>
          <SectionTitle>{t("onb.links.homeScreen")}</SectionTitle>
          <Card className="space-y-1 text-sm text-slate-700">
            <p>{t("onb.links.iphone")}</p>
            <p>{t("onb.links.android")}</p>
          </Card>
        </div>
      </div>
    </div>
  );
}
